import type { JSX } from "react";
import { useEffect, useState } from "react";
import { useParams } from "react-router";
import type { CookLogApiItem, PatchCookLogRequest } from "@recipevault/shared";
import { getCookLogsByRecipe, patchCookLog, deleteCookLog } from "./cook-log-api.js";
import { friendlyError } from "../../shared/http.js";
import Rating from "../recipes/Rating.js";
import type { RatingValue } from "../recipes/Rating.js";

// ---------------------------------------------------------------------------
// Local helpers
// ---------------------------------------------------------------------------

function formatMinutes(n: number | null): string {
  if (n === null) return "—";
  if (n < 60) return `${n}m`;
  const h = Math.floor(n / 60);
  const m = n % 60;
  return m === 0 ? `${h}h` : `${h}h ${m}m`;
}

function formatCookedDate(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}

// ---------------------------------------------------------------------------
// CookLogEntry — renders a single row with edit + delete affordances
// ---------------------------------------------------------------------------

interface CookLogEntryProps {
  item: CookLogApiItem;
  onPatch: (id: string, body: PatchCookLogRequest) => Promise<CookLogApiItem>;
  onDelete: (item: CookLogApiItem) => Promise<void>;
}

function CookLogEntry({ item, onPatch, onDelete }: CookLogEntryProps): JSX.Element {
  const totalMin = item.durationOverride ?? item.durationElapsedMin;

  const [isEditing, setIsEditing] = useState(false);
  const [dishNoteInput, setDishNoteInput] = useState(item.dishNote ?? "");
  const [cookNoteInput, setCookNoteInput] = useState(item.cookNote ?? "");
  const [durationInput, setDurationInput] = useState(
    item.durationOverride !== null ? String(item.durationOverride) : "",
  );
  const [patchError, setPatchError] = useState<string | null>(null);

  // Sync form inputs when the item prop changes (parent splices resolved item back in).
  useEffect(() => {
    setDishNoteInput(item.dishNote ?? "");
    setCookNoteInput(item.cookNote ?? "");
    setDurationInput(item.durationOverride !== null ? String(item.durationOverride) : "");
  }, [item]);

  function handleDishRatingChange(next: RatingValue): void {
    setPatchError(null);
    void onPatch(item.id, { completedAt: item.completedAt, dishRating: next }).catch(
      (err: unknown) => { setPatchError(friendlyError(err)); },
    );
  }

  function handleCookRatingChange(next: RatingValue): void {
    setPatchError(null);
    void onPatch(item.id, { completedAt: item.completedAt, cookRating: next }).catch(
      (err: unknown) => { setPatchError(friendlyError(err)); },
    );
  }

  function handleSave(): void {
    setPatchError(null);
    // Always include notes (null for empty → clears them on the server).
    // durationOverride is omitted entirely when the input is blank, negative,
    // or non-numeric — a negative value would otherwise throw a ZodError in
    // patchCookLog (schema parse runs before the network call).
    const body: PatchCookLogRequest = {
      completedAt: item.completedAt,
      dishNote: dishNoteInput || null,
      cookNote: cookNoteInput || null,
    };
    const dur = durationInput.trim();
    const durNum = parseInt(dur, 10);
    if (dur !== "" && Number.isInteger(durNum) && durNum >= 0) {
      body.durationOverride = durNum;
    }
    void onPatch(item.id, body).catch(
      (err: unknown) => { setPatchError(friendlyError(err)); },
    );
  }

  function handleDelete(): void {
    void onDelete(item);
  }

  return (
    <li role="article" className="border border-zinc-800 rounded-lg p-4 space-y-3">
      {/* Date + times */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1">
        <time className="text-zinc-100 font-medium" dateTime={item.cookedAt}>
          {formatCookedDate(item.cookedAt)}
        </time>
        <div className="flex items-center gap-3 text-sm text-zinc-400 tabular-nums">
          <span>Total: {formatMinutes(totalMin)}</span>
          <span aria-hidden>·</span>
          <span>Active: {formatMinutes(item.durationActiveMin)}</span>
        </div>
      </div>

      {/* Read-only view */}
      {!isEditing && (
        <>
          {(item.cookRating !== null || item.dishRating !== null) && (
            <div className="flex items-center gap-4">
              {item.cookRating !== null && (
                <div className="flex items-center gap-1.5">
                  <span className="text-xs text-zinc-500">Cook</span>
                  <Rating value={item.cookRating} readOnly />
                </div>
              )}
              {item.dishRating !== null && (
                <div className="flex items-center gap-1.5">
                  <span className="text-xs text-zinc-500">Dish</span>
                  <Rating value={item.dishRating} readOnly />
                </div>
              )}
            </div>
          )}
          {item.cookNote && (
            <p className="text-sm text-zinc-300 whitespace-pre-wrap">{item.cookNote}</p>
          )}
          {item.dishNote && (
            <p className="text-sm text-zinc-300 whitespace-pre-wrap">{item.dishNote}</p>
          )}
        </>
      )}

      {/* Edit form */}
      {isEditing && (
        <div className="space-y-4 pt-2 border-t border-zinc-800/60">
          {/* Dish rating — auto-patches on change */}
          <div>
            <p className="text-xs text-zinc-500 mb-1.5">Dish rating</p>
            <div data-testid="dish-rating-input">
              <Rating value={item.dishRating} onChange={handleDishRatingChange} />
            </div>
          </div>

          {/* Cook rating — auto-patches on change */}
          <div>
            <p className="text-xs text-zinc-500 mb-1.5">Cook rating</p>
            <div data-testid="cook-rating-input">
              <Rating value={item.cookRating} onChange={handleCookRatingChange} />
            </div>
          </div>

          {/* Dish note */}
          <div>
            <label
              className="text-xs text-zinc-500 block mb-1"
              htmlFor={`dish-note-${item.id}`}
            >
              Dish note
            </label>
            <textarea
              id={`dish-note-${item.id}`}
              value={dishNoteInput}
              onChange={(e) => { setDishNoteInput(e.target.value); }}
              maxLength={2000}
              rows={2}
              className="w-full bg-zinc-900 border border-zinc-700 rounded p-2 text-sm text-zinc-200 resize-none placeholder-zinc-600 focus:outline-none focus:border-zinc-500"
              placeholder="Your dish note…"
            />
          </div>

          {/* Cook note */}
          <div>
            <label
              className="text-xs text-zinc-500 block mb-1"
              htmlFor={`cook-note-${item.id}`}
            >
              Cook note
            </label>
            <textarea
              id={`cook-note-${item.id}`}
              value={cookNoteInput}
              onChange={(e) => { setCookNoteInput(e.target.value); }}
              maxLength={2000}
              rows={2}
              className="w-full bg-zinc-900 border border-zinc-700 rounded p-2 text-sm text-zinc-200 resize-none placeholder-zinc-600 focus:outline-none focus:border-zinc-500"
              placeholder="Your cook note…"
            />
          </div>

          {/* Correct elapsed time */}
          <div>
            <label
              className="text-xs text-zinc-500 block mb-1"
              htmlFor={`duration-${item.id}`}
            >
              Correct elapsed time (minutes)
            </label>
            <input
              id={`duration-${item.id}`}
              type="number"
              min={0}
              value={durationInput}
              onChange={(e) => { setDurationInput(e.target.value); }}
              className="w-24 bg-zinc-900 border border-zinc-700 rounded p-2 text-sm text-zinc-200 focus:outline-none focus:border-zinc-500 tabular-nums"
              placeholder="—"
            />
          </div>

          {/* Per-row patch error */}
          {patchError !== null && (
            <p className="text-red-400 text-xs" role="alert">{patchError}</p>
          )}

          {/* Save + close */}
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={handleSave}
              className="px-3 py-1.5 text-xs font-medium bg-emerald-600 hover:bg-emerald-500 text-white rounded transition-colors"
            >
              Save
            </button>
            <button
              type="button"
              onClick={() => { setIsEditing(false); setPatchError(null); }}
              className="px-3 py-1.5 text-xs font-medium border border-zinc-700 text-zinc-400 hover:text-zinc-200 rounded transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      )}

      {/* Row-level actions — always visible */}
      <div className="flex items-center gap-3 pt-0.5">
        <button
          type="button"
          aria-expanded={isEditing}
          onClick={() => { setIsEditing((prev) => !prev); setPatchError(null); }}
          className="text-xs text-zinc-500 hover:text-zinc-300 transition-colors"
        >
          Edit
        </button>
        <button
          type="button"
          onClick={handleDelete}
          className="text-xs text-zinc-500 hover:text-red-400 transition-colors ml-auto"
        >
          Delete
        </button>
      </div>
    </li>
  );
}

// ---------------------------------------------------------------------------
// CookLogHistory — the route component
// ---------------------------------------------------------------------------

export function CookLogHistory(): JSX.Element {
  const { id } = useParams<{ id: string }>();

  const [items, setItems] = useState<CookLogApiItem[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Initial load
  useEffect(() => {
    if (!id) return;
    setLoading(true);
    void getCookLogsByRecipe(id)
      .then((res) => {
        setItems(res.items);
        setCursor(res.nextCursor);
      })
      .catch((err: unknown) => {
        setError(friendlyError(err));
      })
      .finally(() => {
        setLoading(false);
      });
  }, [id]);

  async function loadMore(): Promise<void> {
    if (!id || !cursor || loading) return;
    setLoading(true);
    try {
      const res = await getCookLogsByRecipe(id, cursor);
      setItems((prev) => [...prev, ...res.items]);
      setCursor(res.nextCursor);
    } catch (err: unknown) {
      setError(friendlyError(err));
    } finally {
      setLoading(false);
    }
  }

  // Optimistic PATCH: update the row immediately; splice the server response
  // back on success; revert just the affected row on failure.
  function handlePatch(logId: string, body: PatchCookLogRequest): Promise<CookLogApiItem> {
    const prevItem = items.find((i) => i.id === logId);
    setItems((prev) => prev.map((i) => (i.id === logId ? ({ ...i, ...body } as CookLogApiItem) : i)));
    return patchCookLog(logId, body)
      .then((updated) => {
        setItems((prev) => prev.map((i) => (i.id === logId ? updated : i)));
        return updated;
      })
      .catch((err: unknown) => {
        if (prevItem) {
          setItems((prev) => prev.map((i) => (i.id === logId ? prevItem : i)));
        }
        throw err;
      });
  }

  // Optimistic DELETE: remove immediately; restore at the original index on failure.
  async function handleDelete(item: CookLogApiItem): Promise<void> {
    const idx = items.findIndex((i) => i.id === item.id);
    setItems((prev) => prev.filter((i) => i.id !== item.id));
    setError(null);
    try {
      await deleteCookLog(item.id, item.completedAt);
    } catch (err: unknown) {
      setItems((prev) => {
        const next = [...prev];
        next.splice(idx, 0, item);
        return next;
      });
      setError(friendlyError(err));
    }
  }

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-semibold tracking-tight text-zinc-50">Cook log</h1>

      {/* Loading state (only while list is empty) */}
      {loading && items.length === 0 && (
        <p className="text-zinc-500 text-sm">Loading…</p>
      )}

      {/* Error state (fetch errors + delete failures) */}
      {error !== null && (
        <p className="text-destructive text-sm" role="alert">{error}</p>
      )}

      {/* Empty state */}
      {!loading && error === null && items.length === 0 && (
        <p className="text-zinc-500 text-sm">No cooks logged yet.</p>
      )}

      {/* Item list */}
      {items.length > 0 && (
        <ul className="space-y-4">
          {items.map((item) => (
            <CookLogEntry
              key={item.id}
              item={item}
              onPatch={handlePatch}
              onDelete={handleDelete}
            />
          ))}
        </ul>
      )}

      {/* Load more */}
      {cursor !== null && (
        <button
          type="button"
          onClick={() => { void loadMore(); }}
          disabled={loading}
          className="w-full py-2 text-sm text-zinc-400 hover:text-zinc-200 border border-zinc-800 rounded-lg disabled:opacity-50"
        >
          {loading ? "Loading…" : "Load more"}
        </button>
      )}
    </div>
  );
}
